import React, { useCallback, memo, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { List, AutoSizer, InfiniteLoader } from 'react-virtualized';
import clsx from 'clsx';
import { RotatingLines } from 'react-loader-spinner';

import AccessItem from './AccessItem';
import { ICONS } from '../../icons';

import './AccessContainers.scss';

const AccessContainers = ({
  onInactive,
  onActive,
  inactive = [],
  active = [],
  disabled,
  isLoading,
  onLoadMore,
  searchStr,
  totalElements,
  inactiveLoadedSize,
  classes = {
    wrapper: '',
    container: '',
    element: '',
  },
}) => {
  const { t } = useTranslation('projects');
  const [page, setPage] = useState(1);
  const listRef = useRef(null);

  const handleActivate = useCallback(
    (user) => {
      onActive(user);
    },
    [onActive],
  );

  const handleInactivate = useCallback(
    (user) => {
      onInactive(user);
    },
    [onInactive],
  );

  const scrollToIndex = (index) => {
    if (listRef.current) {
      listRef.current.scrollToRow(index);
    }
  };

  const inactiveRowRenderer = ({ index, key, style }) => {
    const user = inactive[index];

    const listItemStyle = {
      ...style,
      marginBottom: 6,
    };

    return (
      <div key={key} style={listItemStyle}>
        <AccessItem
          Icon={ICONS.CirclePlus}
          onClick={handleActivate}
          user={user}
          disabled={disabled}
          searchStr={searchStr}
          extraClassName={classes.element}
        />
      </div>
    );
  };

  const isRowLoaded = useCallback(({ index }) => !!inactive[index], [inactive]);

  const loadMoreRows = () => {
    if (!isLoading && !!onLoadMore && inactiveLoadedSize !== totalElements) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    if (!!onLoadMore && page !== 1 && !isLoading) {
      onLoadMore(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, onLoadMore]);

  useEffect(() => {
    setPage(1);
    scrollToIndex(0);
  }, [searchStr]);

  return (
    <div className={`access_containers`}>
      <div className={clsx(`access_containers_wrapper`, classes.wrapper)}>
        <div className={`access_containers_header`}>
          <span className={`access_containers_header_text`}>{t('inactive')}</span>
        </div>

        <div className={clsx('access_containers_container-test', classes.container)}>
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isRowLoaded={isRowLoaded}
                loadMoreRows={loadMoreRows}
                rowCount={inactive.length + 1}
                threshold={1}>
                {({ onRowsRendered, registerChild }) => (
                  <List
                    ref={(ref) => {
                      listRef.current = ref;
                      registerChild(ref);
                    }}
                    onRowsRendered={onRowsRendered}
                    rowRenderer={inactiveRowRenderer}
                    rowCount={inactive.length}
                    rowHeight={46}
                    width={width}
                    height={height}
                  />
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>

          {isLoading ? (
            <div className='loader-overlay'>
              <RotatingLines strokeColor='#fdbf47' strokeWidth='5' animationDuration='0.75' width={50} visible={true} />
            </div>
          ) : null}
        </div>
      </div>
      <div className={clsx(`access_containers_wrapper`, classes.wrapper)}>
        <div className={`access_containers_header`}>
          <span className={`access_containers_header_text`}>{t('active')}</span>
        </div>

        <div className={clsx(`access_containers_container`, classes.container)}>
          {active.map((user) => (
            <AccessItem
              key={user.id}
              Icon={ICONS.CircleMinus}
              onClick={handleInactivate}
              user={user}
              disabled={disabled}
              extraClassName={classes.element}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(AccessContainers);
